import React, { useEffect, useState, useCallback } from 'react';
import { BarChart3, RefreshCw, Trophy, Target, FileCheck, Users, Percent } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, LineChart, Line, PieChart, Pie, Cell,
} from 'recharts';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const inr = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);
const inrShort = (v) => (v >= 1e7 ? `₹${(v / 1e7).toFixed(1)}Cr` : v >= 1e5 ? `₹${(v / 1e5).toFixed(1)}L` : `₹${Math.round(v || 0).toLocaleString('en-IN')}`);
const monthLabel = (m) => new Date(`${m}-01`).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });

const STATUS_META = {
  draft: { label: 'Draft', color: '#94a3b8' },
  sent: { label: 'Sent', color: '#3b82f6' },
  accepted: { label: 'Accepted', color: '#16a34a' },
  declined: { label: 'Declined', color: '#dc2626' },
  expired: { label: 'Expired', color: '#f59e0b' },
};

const TABS = [
  { id: 'status', label: 'Quote Status Summary', icon: FileCheck },
  { id: 'conversion', label: 'Win Rate & Conversion', icon: Percent },
  { id: 'customers', label: 'Quotes by Customer', icon: Users },
  { id: 'people', label: 'Salesperson Performance', icon: Trophy },
  { id: 'sources', label: 'Sources, Efforts & Lost Reasons', icon: BarChart3 },
];

const Kpi = ({ id, label, value, sub, icon: Icon }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`kpi-${id}`}>
    <div className="flex items-center justify-between">
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
      <Icon className="w-4 h-4 text-slate-400" />
    </div>
    <p className="text-2xl font-bold text-slate-900 mt-2">{value}</p>
    {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
  </div>
);

const Card = ({ title, children, testId }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid={testId}>
    <h3 className="text-sm font-semibold text-slate-800 mb-4">{title}</h3>
    {children}
  </div>
);

const Table = ({ cols, rows, testId, empty = 'No data for the selected filters' }) => (
  <div className="overflow-x-auto" data-testid={testId}>
    <table className="w-full text-sm">
      <thead className="bg-slate-50 border-b border-slate-200">
        <tr>{cols.map(c => <th key={c.key} className={`px-3 py-2 text-xs font-medium text-slate-500 uppercase ${c.right ? 'text-right' : 'text-left'}`}>{c.label}</th>)}</tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.length === 0 ? (
          <tr><td colSpan={cols.length} className="px-3 py-8 text-center text-slate-400">{empty}</td></tr>
        ) : rows.map((r, i) => (
          <tr key={r.name || r.status || i} className="hover:bg-slate-50">
            {cols.map(c => <td key={c.key} className={`px-3 py-2 ${c.right ? 'text-right tabular-nums' : ''}`}>{c.render ? c.render(r) : r[c.key]}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const StatusView = ({ data }) => {
  const rows = data.quote_status_summary.map(r => ({ ...r, ...STATUS_META[r.status] }));
  const pie = rows.filter(r => r.count > 0);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card title="Quotes by status (count)" testId="status-pie-card">
        {pie.length === 0 ? <p className="text-sm text-slate-400 py-16 text-center">No quotes yet</p> : (
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={pie} dataKey="count" nameKey="label" innerRadius={60} outerRadius={100} paddingAngle={2}>
                {pie.map(p => <Cell key={p.status} fill={p.color} />)}
              </Pie>
              <Tooltip /><Legend />
            </PieChart>
          </ResponsiveContainer>
        )}
      </Card>
      <Card title="Status summary" testId="status-summary-card">
        <Table testId="status-summary-table" rows={rows} cols={[
          { key: 'label', label: 'Status', render: r => <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: r.color }} />{r.label}</span> },
          { key: 'count', label: 'Quotes', right: true },
          { key: 'value', label: 'Value', right: true, render: r => inr(r.value) },
        ]} />
      </Card>
    </div>
  );
};

const ConversionView = ({ data }) => {
  const monthly = data.monthly.map(m => ({ ...m, label: monthLabel(m.month) }));
  return (
    <div className="space-y-6">
      <Card title="Monthly funnel: enquiries → quoted → won" testId="monthly-funnel-chart">
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={monthly}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="label" fontSize={12} /><YAxis allowDecimals={false} fontSize={12} />
            <Tooltip /><Legend />
            <Bar dataKey="enquiries" name="Enquiries" fill="#64748b" radius={[4, 4, 0, 0]} />
            <Bar dataKey="quoted" name="Quoted" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
            <Bar dataKey="won" name="Won" fill="#16a34a" radius={[4, 4, 0, 0]} />
            <Bar dataKey="lost" name="Lost" fill="#ef4444" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Win rate trend (%)" testId="win-rate-chart">
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="label" fontSize={12} /><YAxis domain={[0, 100]} fontSize={12} />
              <Tooltip /><Line type="monotone" dataKey="win_rate" name="Win rate %" stroke="#16a34a" strokeWidth={2} dot />
            </LineChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Win rate by salesperson" testId="win-rate-by-person">
          <Table rows={data.by_salesperson} cols={[
            { key: 'name', label: 'Salesperson' },
            { key: 'enquiries', label: 'Enq.', right: true },
            { key: 'won', label: 'Won', right: true },
            { key: 'win_rate', label: 'Win %', right: true, render: r => `${r.win_rate}%` },
            { key: 'conversion', label: 'Conv. %', right: true, render: r => `${r.conversion}%` },
          ]} />
        </Card>
      </div>
    </div>
  );
};

const CustomersView = ({ data }) => (
  <div className="space-y-6">
    <Card title="Top customers by quoted value" testId="customer-chart">
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={data.by_customer.slice(0, 10).map(c => ({ ...c, short: c.name.length > 28 ? `${c.name.slice(0, 26)}…` : c.name }))} layout="vertical" margin={{ left: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis type="number" tickFormatter={inrShort} fontSize={12} />
          <YAxis type="category" dataKey="short" width={200} fontSize={11} interval={0} />
          <Tooltip formatter={(v) => inr(v)} /><Legend />
          <Bar dataKey="won_value" name="Won" stackId="a" fill="#16a34a" />
          <Bar dataKey="open_value" name="Open" stackId="a" fill="#3b82f6" />
          <Bar dataKey="lost_value" name="Lost" stackId="a" fill="#ef4444" />
        </BarChart>
      </ResponsiveContainer>
    </Card>
    <Card title="Quotes by customer" testId="customer-table-card">
      <Table testId="customer-table" rows={data.by_customer} cols={[
        { key: 'name', label: 'Customer', render: r => <span className="font-medium text-slate-800">{r.name}</span> },
        { key: 'enquiries', label: 'Enquiries', right: true },
        { key: 'quotes', label: 'Quotes', right: true },
        { key: 'quoted_value', label: 'Quoted', right: true, render: r => inr(r.quoted_value) },
        { key: 'won_value', label: 'Won', right: true, render: r => <span className="text-green-700">{inr(r.won_value)}</span> },
        { key: 'open_value', label: 'Open', right: true, render: r => inr(r.open_value) },
        { key: 'lost_value', label: 'Lost', right: true, render: r => <span className="text-red-600">{inr(r.lost_value)}</span> },
        { key: 'win_rate', label: 'Win % (value)', right: true, render: r => `${r.win_rate}%` },
      ]} />
    </Card>
  </div>
);

const PeopleView = ({ data }) => (
  <div className="space-y-6">
    <Card title="Efforts vs results by salesperson" testId="people-chart">
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={data.by_salesperson}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis dataKey="name" fontSize={12} /><YAxis allowDecimals={false} fontSize={12} />
          <Tooltip /><Legend />
          <Bar dataKey="followups" name="Follow-ups" fill="#f97316" radius={[4, 4, 0, 0]} />
          <Bar dataKey="enquiries" name="Enquiries" fill="#94a3b8" radius={[4, 4, 0, 0]} />
          <Bar dataKey="quoted" name="Quoted" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
          <Bar dataKey="won" name="Won" fill="#16a34a" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </Card>
    <Card title="Salesperson performance" testId="people-table-card">
      <Table testId="people-table" rows={data.by_salesperson} cols={[
        { key: 'name', label: 'Salesperson', render: r => <span className="font-medium text-slate-800">{r.name}</span> },
        { key: 'followups', label: 'Follow-ups', right: true, render: r => `${r.followups_done}/${r.followups}` },
        { key: 'enquiries', label: 'Enquiries', right: true },
        { key: 'quoted', label: 'Quoted', right: true },
        { key: 'quoted_value', label: 'Quoted Value', right: true, render: r => inr(r.quoted_value) },
        { key: 'won', label: 'Won', right: true },
        { key: 'won_value', label: 'Won Value', right: true, render: r => <span className="text-green-700 font-medium">{inr(r.won_value)}</span> },
        { key: 'win_rate', label: 'Win %', right: true, render: r => `${r.win_rate}%` },
      ]} />
    </Card>
  </div>
);

const EFFORT_LABELS = { cold_call: 'Cold calls', site_visit: 'Site visits', call_back: 'Call backs', visit_later: 'Visit later', general: 'General' };

const SourcesView = ({ data }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="efforts-by-type">
      {Object.keys(EFFORT_LABELS).map(k => (
        <div key={k} className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500">{EFFORT_LABELS[k]} done</p>
          <p className="text-2xl font-bold text-slate-900">{data.efforts_by_type?.[k] || 0}</p>
        </div>
      ))}
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card title="Lead source → enquiries → wins (marketing)" testId="source-card">
        <Table testId="source-table" rows={(data.by_source || []).map(r => ({ ...r, name: r.source }))} cols={[
          { key: 'source', label: 'Source', render: r => <span className="font-medium text-slate-800">{r.source}</span> },
          { key: 'enquiries', label: 'Enquiries', right: true },
          { key: 'won', label: 'Won', right: true },
          { key: 'won_value', label: 'Won Value', right: true, render: r => inr(r.won_value) },
          { key: 'conversion', label: 'Conv. %', right: true, render: r => `${r.conversion}%` },
        ]} />
      </Card>
      <Card title="Why we lose (declined quotes)" testId="lost-reason-card">
        <Table testId="lost-reason-table" empty="No declined quotes in this period" rows={(data.by_lost_reason || []).map(r => ({ ...r, name: r.reason }))} cols={[
          { key: 'reason', label: 'Reason', render: r => <span className="font-medium text-slate-800">{r.reason}</span> },
          { key: 'count', label: 'Quotes', right: true },
          { key: 'value', label: 'Value Lost', right: true, render: r => <span className="text-red-600">{inr(r.value)}</span> },
        ]} />
      </Card>
    </div>
  </div>
);

const VIEWS = { status: StatusView, conversion: ConversionView, customers: CustomersView, people: PeopleView, sources: SourcesView };

export default function SalesAnalysis() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('status');
  const [fy, setFy] = useState('');
  const [person, setPerson] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (fy) qs.set('fy', fy);
      if (person) qs.set('salesperson', person);
      const res = await fetch(`${API_URL}/api/sales-crm/analytics?${qs}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (!res.ok) throw new Error();
      setData(await res.json());
    } catch {
      toast.error('Failed to load sales analysis');
    } finally {
      setLoading(false);
    }
  }, [fy, person]);

  useEffect(() => { load(); }, [load]);

  const s = data?.summary || {};
  const View = VIEWS[tab];

  return (
    <div className="space-y-6 max-w-[calc(100vw-3rem)] md:max-w-none" data-testid="sales-analysis-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><BarChart3 className="w-6 h-6 text-purple-600" /> Sales Analysis</h1>
          <p className="text-slate-500 mt-1 text-sm">Sales &amp; marketing efforts and results, from enquiries, quotes and follow-ups</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={fy} onChange={e => setFy(e.target.value)} data-testid="analysis-fy-filter" className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white">
            <option value="">All years</option>
            {(data?.filters.fiscal_years || []).map(y => <option key={y} value={y}>FY{String(y).slice(2)}-{String(y + 1).slice(2)}</option>)}
          </select>
          <select value={person} onChange={e => setPerson(e.target.value)} data-testid="analysis-person-filter" className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white">
            <option value="">All salespeople</option>
            {(data?.filters.salespeople || []).map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <button onClick={load} data-testid="analysis-refresh-btn" className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg"><RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} /></button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi id="enquiries" label="Enquiries" value={s.enquiries || 0} sub={`${s.quote_rate || 0}% quoted`} icon={FileCheck} />
        <Kpi id="quoted-value" label="Quoted Value" value={inrShort(s.quoted_value)} sub={`${s.quoted || 0} quotes`} icon={FileCheck} />
        <Kpi id="won-value" label="Won Value" value={inrShort(s.won_value)} sub={`${s.won || 0} won`} icon={Trophy} />
        <Kpi id="win-rate" label="Win Rate" value={`${s.win_rate || 0}%`} sub={`${s.won || 0} won / ${s.lost || 0} lost`} icon={Target} />
        <Kpi id="conversion" label="Conversion" value={`${s.conversion_rate || 0}%`} sub="Enquiry to win" icon={Percent} />
        <Kpi id="followups" label="Follow-ups" value={`${s.followups_done || 0}/${s.followups || 0}`} sub="Completed / total" icon={Users} />
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-slate-200">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`analysis-tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${tab === t.id ? 'border-purple-600 text-purple-700' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>

      {data && <View data={data} />}
    </div>
  );
}
